import { Close, Search } from "@mui/icons-material";
import { Drawer, TextField, Typography, useTheme } from "@mui/material";
import React, { useEffect } from "react";
import { Col, Row } from "v2/components/base/Flex/ui";
import Scrollable from "v2/components/base/Scrollable/ui";
import { ReactComponent as DarkLogoIcon } from "assets/icons/logo-dark.svg";

const SearchDrawer = ({
  handleClose,
  handleSearch,
  debouncedSearch,
  data,
  isLoadingData,
  isLoadedData,
  isLoading,
  isOpenned,
  isEmpty,
  hasAllFetched,
  loadMoreRef,
  children,
  ...props
}: {
  children: React.ReactNode;
  handleClose: () => void;
  handleSearch: (search: string | null) => void;
  debouncedSearch: string | null;
  data: any[];
  isLoadingData: boolean;
  isLoadedData: boolean;
  isLoading: boolean;
  isOpenned: boolean;
  isEmpty: boolean;
  hasAllFetched: boolean;
  loadMoreRef: (node?: Element | null) => void;
}) => {
  const theme = useTheme();

  return (
    <React.Fragment key={"bottom"}>
      <Drawer
        anchor={"bottom"}
        open={isOpenned}
        sx={{
          "& .MuiDrawer-paper": {
            width: "100%",
            height: "100%",
            backgroundColor: theme.palette.surface.bg,
            color: theme.palette.text.primary,
            display: "flex",
            justifyContent: "start",
            alignItems: "center",
          },
        }}>
        <SearchBar
          placeholder="Search token (name or address)"
          handleChange={handleSearch}
          handleClose={handleClose}
        />
        <Row sx={{ padding: "8px 10px" }} fullWidth fullHeight>
          {debouncedSearch ? (
            !data || data.length <= 0 ? (
              <Col
                type="cc"
                sx={{
                  margin: "auto",
                  gap: "8px",
                  textAlign: "center",
                  maxWidth: "282px",
                }}>
                <DarkLogoIcon />
                {!isLoadedData || isLoading ? (
                  <Typography variant="title-h3">Loading...</Typography>
                ) : (
                  <>
                    <Typography variant="title-h3">Result not found</Typography>
                    <Typography variant="body-sm">Please try again.</Typography>
                  </>
                )}
              </Col>
            ) : (
              <Scrollable
                isLoading={isLoading}
                isEmpty={isEmpty}
                hasAllFetched={hasAllFetched}
                loadMoreRef={loadMoreRef}>
                {children}
              </Scrollable>
            )
          ) : null}
        </Row>
      </Drawer>
    </React.Fragment>
  );
};

const SearchBar = ({
  placeholder,
  handleChange,
  handleClose,
}: {
  placeholder: string;
  handleChange: (value: string) => void;
  handleClose: () => void;
}) => {
  const theme = useTheme();
  return (
    <Row
      type="bc"
      sx={{
        padding: "19px 25px",
        backgroundColor: theme.palette.surface.container,
      }}
      fullWidth>
      <Row type="bc" fullWidth>
        <Search />
        <SearchBarTextField placeholder={placeholder} handleChange={handleChange} />
      </Row>
      <Row type="cc" gap="16px" fullHeight>
        <Close sx={{ cursor: "pointer" }} onClick={handleClose} />
      </Row>
    </Row>
  );
};

const SearchBarTextField = ({
  placeholder,
  handleChange,
}: {
  placeholder: string;
  handleChange: (value: string) => void;
}) => {
  const theme = useTheme();
  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(e.target.value);
  };
  return (
    <TextField
      sx={{
        backgroundColor: theme.palette.surface.container,
        borderRadius: "12px",
        width: "100%",

        "& .MuiOutlinedInput-notchedOutline": {
          border: "none",
        },

        "& .MuiSelect-icon": {
          color: theme.palette.text.secondary,
          fontSize: "24px",
        },

        "& .MuiInputBase-input": {
          padding: "8px",
          ...theme.typography["body-md"],

          "&::placeholder": {
            color: theme.palette.text.secondary,
          },
        },

        "& .MuiSvgIcon-root": {
          color: theme.palette.text.secondary,
        },
      }}
      placeholder={placeholder}
      onChange={handleOnChange}
    />
  );
};

export default SearchDrawer;
